export interface ConventionPerformerVisibility {
  /**
   * Reveal this performer from midnight in the UK on this date.
   * Use YYYY-MM-DD, for example: 2026-09-01.
   */
  launchDate?: string

  /**
   * Existing manual visibility switch. A launchDate takes precedence when set.
   */
  isLive?: boolean
}

export const PERFORMER_LAUNCH_TIME_ZONE = 'Europe/London'

const launchDatePattern = /^(\d{4})-(\d{2})-(\d{2})$/

const launchDateFormatter = new Intl.DateTimeFormat('en-GB', {
  timeZone: PERFORMER_LAUNCH_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
})

const isValidLaunchDate = (launchDate: string) => {
  const match = launchDate.match(launchDatePattern)
  if (!match) return false

  const [, yearText, monthText, dayText] = match
  const year = Number(yearText)
  const month = Number(monthText)
  const day = Number(dayText)
  const parsed = new Date(Date.UTC(year, month - 1, day))

  return (
    parsed.getUTCFullYear() === year &&
    parsed.getUTCMonth() === month - 1 &&
    parsed.getUTCDate() === day
  )
}

const formatDateInLaunchTimeZone = (date: Date) => {
  const parts = launchDateFormatter.formatToParts(date)
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find(value => value.type === type)?.value

  return `${part('year')}-${part('month')}-${part('day')}`
}

export const isConventionPerformerVisible = (
  performer: ConventionPerformerVisibility,
  now = new Date()
) => {
  if (performer.launchDate === undefined) {
    return performer.isLive !== false
  }

  if (!isValidLaunchDate(performer.launchDate)) {
    throw new Error(
      `Invalid performer launch date "${performer.launchDate}". Use YYYY-MM-DD.`
    )
  }

  return formatDateInLaunchTimeZone(now) >= performer.launchDate
}

export const getVisibleConventionPerformers = <
  T extends ConventionPerformerVisibility,
>(
  performers: readonly T[],
  now = new Date()
) =>
  performers.filter(performer => isConventionPerformerVisible(performer, now))
